(() => {
  'use strict';
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[character]));

  class AAAccessSettings {
    constructor(root) {
      this.root = root;
      this.content = root.querySelector('[data-aa-access-content]');
      this.tab = 'users';
      this.data = null;
      this.selectedClient = '';
      root.querySelectorAll('[data-aa-access-tab]').forEach(button => {
        button.addEventListener('click', () => {
          this.tab = button.dataset.aaAccessTab;
          root.querySelectorAll('[data-aa-access-tab]').forEach(tab => {
            const selected = tab.dataset.aaAccessTab === this.tab;
            tab.classList.toggle('is-active', selected);
            tab.setAttribute('aria-pressed', String(selected));
          });
          if (this.data) this.render();
        });
      });
      root.addEventListener('submit', event => {
        const form=event.target.closest('[data-aa-role-form]');
        if(!form) return;
        event.preventDefault();
        this.saveRole(form);
      });
      root.addEventListener('change',event=>{
        const client=event.target.closest('[data-aa-access-client]');
        if(client){this.selectedClient=client.value;this.loadClientMembers();return;}
        const member=event.target.closest('[data-aa-member-id]');
        if(member)this.saveMemberAccess(member);
      });
      this.load();
    }

    async load() {
      try {
        const db = window.AASupabaseClient?.getClient?.();
        const cfg = window.AA_SUPABASE_CONFIG;
        if (!db || !cfg?.url || !cfg?.anonKey) throw new Error('Headquarters connection is unavailable.');
        const { data, error } = await db.auth.getSession();
        if (error || !data?.session?.access_token) {
          throw new Error('Your Headquarters session needs to be refreshed. Sign in again.');
        }
        const response = await fetch(cfg.url + '/functions/v1/aa-access-service', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + data.session.access_token,
            apikey: cfg.anonKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action: 'overview' })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.success) {
          const email = payload.signedInEmail ? ' Signed in: ' + payload.signedInEmail : '';
          throw new Error((payload.error || 'Cannot load access settings.') + email);
        }
        this.data = payload.data;
        this.render();
      } catch (error) {
        this.content.innerHTML = '<div class="aa-settings-notice" role="status"><strong>Access settings could not load.</strong><p>' +
          escapeHtml(error?.message || 'Please try again.') +
          '</p><button type="button" class="aa-button aa-button--secondary" data-aa-access-retry>Try again</button></div>';
        this.content.querySelector('[data-aa-access-retry]')?.addEventListener('click', () => this.load());
      }
    }

    render() {
      if (this.tab === 'roles') this.renderRoles();
      else this.renderTeam();
    }

    renderTeam() {
      const members = Array.isArray(this.data?.members) ? this.data.members : [];
      const active = members.filter(person => person.active).length;
      const cards = members.map(person => `
        <article class="aa-settings-person">
          <div>
            <strong>${escapeHtml(person.name || person.email || 'Team member')}</strong>
            ${person.email ? '<p>' + escapeHtml(person.email) + '</p>' : ''}
          </div>
          <div class="aa-settings-person__meta">
            <span>${escapeHtml(person.roleName || person.roleKey || 'Unassigned')}</span>
            <span class="aa-settings-person__state">${person.active ? 'Active' : 'Inactive'}</span>
          </div>
        </article>
      `).join('');
      this.content.innerHTML = `
        <div class="aa-settings-summary"><strong>${active} active team member${active === 1 ? '' : 's'}</strong><span>Account editing will be enabled after permission enforcement is tested.</span></div>
        <div class="aa-settings-people">${cards || '<p>No team members are configured.</p>'}</div>
      `;
    }


    async request(action,payload={}) {
      const db = window.AASupabaseClient?.getClient?.();
      const config = window.AA_SUPABASE_CONFIG;
      if (!db || !config?.url || !config?.anonKey) throw new Error('Headquarters is not connected.');
      const {data,error} = await db.auth.getSession();
      if(error || !data?.session?.access_token) throw new Error('Sign in to Headquarters again.');
      const response = await fetch(config.url + '/functions/v1/aa-access-service',{
        method:'POST',
        headers:{Authorization:'Bearer '+data.session.access_token,apikey:config.anonKey,'Content-Type':'application/json'},
        body:JSON.stringify({action,...payload})
      });
      const result = await response.json().catch(()=>({}));
      if(!response.ok || !result.success) throw new Error(result.error||'Could not save access changes.');
      return result.data||{};
    }

    clientDefaults(key) {
      const role=(this.data.roles||[]).find(role=>role.role_key===key);
      return new Set((this.data.rolePermissions||[]).filter(grant=>grant.role_id===role?.id).map(grant=>grant.permission_key));
    }

    renderRoles() {
      const roles=Array.isArray(this.data?.roles)?this.data.roles:[];
      const allPermissions=(this.data.permissions||[]).filter(p=>p.scope==='client');
      const names={
        'client.playbooks.view':'Open marketing playbooks',
        'client.playbooks.edit':'Edit marketing playbooks',
        'client.catalogs.view':'View catalogs',
        'client.invoices.view':'View invoices',
        'client.contracts.view':'View agreements',
        'client.package_changes.request':'Request package changes',
        'client.package_changes.approve':'Approve package changes',
        'client.team.manage':'Manage client employees'
      };
      const sections=[
        {label:'Marketing',keys:['client.playbooks.view','client.playbooks.edit','client.catalogs.view']},
        {label:'Business & billing',keys:['client.invoices.view','client.contracts.view']},
        {label:'Package requests',keys:['client.package_changes.request']}
      ];
      const profiles=[
        {key:'client_manager',label:'Client Manager',hint:'Playbook editing by default. Everything else is optional.'},
        {key:'client_employee',label:'Client Contributor',hint:'Playbook editing and catalog access. Not a view-only account.'}
      ];
      const owner=this.clientDefaults('client_owner');
      const makeOptions=profile => {
        const granted=this.clientDefaults(profile.key);
        return '<form class="aa-access-profile" data-aa-role-form="'+profile.key+'"><details class="aa-access-profile-details">'+
          '<summary class="aa-access-profile__top"><div><h4>'+profile.label+'</h4><p>'+profile.hint+'</p></div>'+
          '<span class="aa-access-profile__tag">Edit defaults · '+granted.size+' enabled</span></summary>'+
          sections.map(group=>
            '<div class="aa-access-permission-group"><h5>'+group.label+'</h5>'+
            '<div class="aa-access-permission-grid">'+group.keys.map(key=>
              '<label class="aa-access-permission-option"><input type="checkbox" name="roleAccess" value="'+key+'" '+(granted.has(key)?'checked':'')+'><span>'+escapeHtml(names[key]||key)+'</span></label>'
            ).join('')+'</div></div>'
          ).join('')+
          '<div class="aa-access-profile__footer"><span>These are starting permissions. Individual employees can have different access.</span>'+
          '<button type="submit" class="aa-button aa-button--secondary">Save defaults</button></div></details></form>';
      };
      this.content.innerHTML=
        '<p class="aa-access-description">Give each person only the access they need. Managers and contributors do <strong>not</strong> automatically see invoices, contracts, or package changes.</p>'+
        '<p class="aa-access-feedback" role="status" data-aa-role-status></p>'+
        '<section class="aa-settings-role-group"><h3>Client access profiles</h3>'+
          '<article class="aa-access-profile aa-access-profile--locked"><div class="aa-access-profile__top"><div>'+
          '<h4>Client Owner</h4><p>Full access to their own client account, including package approvals and team management.</p></div>'+
          '<span class="aa-access-profile__tag">Protected</span></div>'+
          '<p class="aa-access-profile__meta">'+owner.size+' permissions · Owner access is not editable here.</p></article>'+
          profiles.map(makeOptions).join('')+'</section>'+
        '<section class="aa-settings-role-group aa-access-client-override"><h3>Individual client access</h3>'+
          '<p>Customize access for a specific employee without changing everyone else who has the same role.</p>'+
          '<label class="aa-access-client-label">Client <select data-aa-access-client><option value="">Choose a client...</option>'+
          (this.data.clients||[]).map(client=>
            '<option value="'+escapeHtml(client.id)+'" '+(this.selectedClient===client.id?'selected':'')+'>'+escapeHtml(client.name)+'</option>'
          ).join('')+'</select></label>'+
          '<div data-aa-access-members><p class="aa-access-helper">'+
          (this.selectedClient?'Loading this client team...':'Select a client to view their linked team accounts.')+
          '</p></div></section>'+
        '<section class="aa-settings-role-group aa-access-internal"><h3>Headquarters roles</h3>'+
          '<div class="aa-settings-role-list">'+roles.filter(role=>role.role_key.startsWith('aa_')).map(role=>{
            const items=(this.data.rolePermissions||[]).filter(grant=>grant.role_id===role.id).map(grant=>
              (this.data.permissions||[]).find(item=>item.permission_key===grant.permission_key)?.description||grant.permission_key);
            return '<article class="aa-settings-role"><strong>'+escapeHtml(role.display_name)+'</strong><p>'+
              escapeHtml(items.join(' · '))+'</p></article>';
          }).join('')+'</div></section>';
      if(this.selectedClient) this.loadClientMembers();
    }

    async saveRole(form) {
      const key=form.dataset.aaRoleForm;
      const permissions=[...form.querySelectorAll('input[name="roleAccess"]:checked')].map(input=>input.value);
      const status=this.content.querySelector('[data-aa-role-status]');
      if(permissions.includes('client.playbooks.edit')&&!permissions.includes('client.playbooks.view')){
        status.textContent='Playbook editing requires permission to open playbooks.';
        return;
      }
      const button=form.querySelector('[type="submit"]');
      button.disabled=true;
      status.textContent='Saving role defaults...';
      try {
        const result=await this.request('save-role-defaults',{roleKey:key,permissionKeys:permissions});
        const role=(this.data.roles||[]).find(role=>role.role_key===key);
        this.data.rolePermissions=(this.data.rolePermissions||[]).filter(grant=>grant.role_id!==role.id)
          .concat(result.permissionKeys.map(p=>({role_id:role.id,permission_key:p})));
        this.renderRoles();
        this.content.querySelector('[data-aa-role-status]').textContent='Role defaults saved.';
      } catch(error){
        status.textContent=error.message;
        button.disabled=false;
      }
    }

    async loadClientMembers() {
      const host=this.content.querySelector('[data-aa-access-members]');
      if(!host)return;
      const clientId=this.selectedClient;
      if(!clientId){host.textContent='Choose a client.';return;}
      host.textContent='Loading client employees...';
      try{
        const result=await this.request('client-members',{clientId});
        if(clientId!==this.selectedClient)return;
        this.renderClientMembers(result.members||[]);
      }catch(error){host.textContent=error.message;}
    }

    renderClientMembers(members) {
      const host=this.content.querySelector('[data-aa-access-members]');
      if(!host)return;
      if(!members.length){
        host.innerHTML='<p class="aa-access-helper">This client has no employees linked to the new permission system yet. Existing client login access has not been changed.</p>';
        return;
      }
      const definitions=(this.data.permissions||[]).filter(p=>p.scope==='client' && !['client.package_changes.approve','client.team.manage'].includes(p.permission_key));
      host.innerHTML=members.map(person=>{
        const defaults=new Set(person.defaults||[]);
        const overrides=new Map((person.overrides||[]).map(o=>[o.permission_key,o.is_allowed]));
        const controls=person.roleKey==='client_owner'
          ? '<p>Owners have full access to their own account.</p>'
          : '<div class="aa-access-member-controls">'+definitions.map(def=>{
              const override=overrides.get(def.permission_key);
              const choice=override===true?'allow':override===false?'block':'default';
              return '<label class="aa-access-member-control"><span>'+escapeHtml(def.description)+'</span>'+
                '<select data-aa-member-id="'+escapeHtml(person.id)+'" data-aa-member-key="'+escapeHtml(def.permission_key)+'" '+(!person.active?'disabled':'')+'>'+
                '<option value="default" '+(choice==='default'?'selected':'')+'>Role default ('+(defaults.has(def.permission_key)?'Allowed':'Not allowed')+')</option>'+
                '<option value="allow" '+(choice==='allow'?'selected':'')+'>Allow</option>'+
                '<option value="block" '+(choice==='block'?'selected':'')+'>Block</option>'+
                '</select></label>';
            }).join('')+'</div>';
        return '<details class="aa-access-member-editor"><summary><strong>'+escapeHtml(person.email||'Client employee')+
          '</strong><span>'+escapeHtml(person.roleName)+' · '+(person.active?'Active':'Inactive')+'</span></summary>'+
          controls+'</details>';
      }).join('');
    }

    async saveMemberAccess(select) {
      const membershipId=select.dataset.aaMemberId;
      const permissionKey=select.dataset.aaMemberKey;
      const value=select.value==='default'?null:select.value==='allow';
      select.disabled=true;
      try{
        await this.request('set-member-permission',{membershipId,permissionKey,value});
        await this.loadClientMembers();
      }catch(error){
        const host=this.content.querySelector('[data-aa-access-members]');
        const errorLabel=document.createElement('p');
        errorLabel.className='aa-access-feedback';
        errorLabel.setAttribute('role','status');
        errorLabel.textContent=error.message;
        host?.prepend(errorLabel);
        select.disabled=false;
      }
    }

  }

  document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('[data-aa-access-settings]');
    if (root) new AAAccessSettings(root);
  });
})();
